/**
 * Viewpoint search-planning module.
 * Mirrors WebSpark's selectView planner so dashboard-generated Jig cameras follow
 * the same requested, silhouette, outward, fine-tune, and lift search path.
 */
import type { ViewpointBounds, ViewpointPreset, ViewpointSceneContext, ViewpointVector3 } from '../generate-jig.types'
import { VIEWPOINT_PRESETS, VIEWPOINT_DISTANCE_MULTIPLIERS } from './viewpoint-presets'
import type { ViewCandidate, ViewpointFamily, ViewSelectionResult } from './view-decision-types'
import { compareVisibilityMetrics, scoreCandidate } from './visibility-scorer'
import {
  add,
  computeAabbFramedDistance,
  diagonal,
  directionToYawPitch,
  dot,
  normalize,
  projectedArea,
  scale,
  sub,
  yawPitchToDirection,
} from './viewpoint-math'

interface SeedCandidate {
  sourceSeed: ViewCandidate['sourceSeed']
  preset: ViewpointPreset
  direction: ViewpointVector3
}

interface PlannerOptions {
  sceneContext: ViewpointSceneContext
  targetBounds: ViewpointBounds
  requestedPreset: ViewpointPreset
  explicitDistance?: number
  minDistance: number
  targetElementIds: Set<string>
}

const VIEW_SEARCH_FOV_DEG = 60
const VIEW_SEARCH_ASPECT = 16 / 9
const VIEW_SEARCH_FRAME_FILL = 0.9
const VIEW_DISTANCE_BUFFER = 1.15
const VISIBILITY_EPSILON = 1e-3
const REQUESTED_VISIBILITY_KEEP_THRESHOLD = 0.55

export const VIEWPOINT_FAMILIES: Record<ViewpointPreset, ViewpointFamily> = {
  front: 'front',
  back: 'back',
  top: 'top',
  side: 'side',
  left: 'left',
  'close-up': 'front',
  wide: 'front',
  'low-angle': 'front',
  'high-angle': 'front',
  'rear-high-angle': 'back',
}

export function getViewpointFamily(preset: ViewpointPreset): ViewpointFamily {
  return VIEWPOINT_FAMILIES[preset]
}

function rankCandidates(
  candidates: ViewCandidate[],
  requestedDirection: ViewpointVector3,
  sceneContext: ViewpointSceneContext,
  targetBounds: ViewpointBounds,
  targetElementIds: Set<string>
): ViewCandidate[] {
  candidates.forEach(function (candidate: ViewCandidate) {
    candidate.metrics = scoreCandidate(candidate, {
      sceneContext: sceneContext,
      targetBounds: targetBounds,
      targetElementIds: targetElementIds,
      requestedDirection: requestedDirection,
    })
  })

  return candidates.slice().sort(function (a: ViewCandidate, b: ViewCandidate) {
    const comparison = compareVisibilityMetrics(a.metrics!, b.metrics!, a.distance, b.distance)
    if (comparison !== 0) {
      return comparison
    }

    return a.sourceSeed.localeCompare(b.sourceSeed)
  })
}

function buildCameraPosition(
  targetCenter: ViewpointVector3,
  direction: ViewpointVector3,
  distance: number,
  liftOffset: number
): ViewpointVector3 {
  return add(add(targetCenter, scale(direction, distance)), { x: 0, y: liftOffset, z: 0 })
}

function computeCandidateDistance(
  targetBounds: ViewpointBounds,
  direction: ViewpointVector3,
  preset: ViewpointPreset,
  explicitDistance: number | undefined,
  minDistance: number
): number {
  if (typeof explicitDistance === 'number' && isFinite(explicitDistance) && explicitDistance > 0) {
    return explicitDistance
  }

  const diagonalDistance = Math.max(diagonal(targetBounds.size) * VIEWPOINT_DISTANCE_MULTIPLIERS[preset], minDistance)
  const framedDistance = computeAabbFramedDistance(targetBounds, direction, minDistance, {
    fovDeg: VIEW_SEARCH_FOV_DEG,
    aspect: VIEW_SEARCH_ASPECT,
    frameFill: VIEW_SEARCH_FRAME_FILL,
  })

  return Math.max(diagonalDistance, framedDistance) * VIEW_DISTANCE_BUFFER
}

function buildLiftCandidates(
  candidates: ViewCandidate[],
  sceneContext: ViewpointSceneContext,
  targetCenter: ViewpointVector3
): ViewCandidate[] {
  const sceneHeight = sceneContext.sceneBounds.size.y
  const liftCandidates: ViewCandidate[] = []

  candidates
    .filter(function (candidate: ViewCandidate) {
      return candidate.liftOffset === 0
    })
    .slice(0, 2)
    .forEach(function (candidate: ViewCandidate) {
      ;[sceneHeight * 0.25, sceneHeight * 0.5].forEach(function (liftOffset: number) {
        liftCandidates.push({
          sourceSeed: candidate.sourceSeed,
          sourcePreset: candidate.sourcePreset,
          direction: candidate.direction,
          targetCenter: candidate.targetCenter,
          distance: candidate.distance,
          liftOffset: liftOffset,
          cameraPosition: buildCameraPosition(targetCenter, candidate.direction, candidate.distance, liftOffset),
        })
      })
    })

  return liftCandidates
}

function dedupeCandidates(candidates: ViewCandidate[]): ViewCandidate[] {
  const seen: Record<string, boolean> = {}
  const deduped: ViewCandidate[] = []

  candidates.forEach(function (candidate: ViewCandidate) {
    const direction = normalize(candidate.direction)
    const key = [
      direction.x.toFixed(3),
      direction.y.toFixed(3),
      direction.z.toFixed(3),
      candidate.liftOffset.toFixed(3),
    ].join(':')

    if (seen[key]) {
      return
    }

    seen[key] = true
    deduped.push(candidate)
  })

  return deduped
}

function findSilhouettePreset(requestedPreset: ViewpointPreset, targetBounds: ViewpointBounds): ViewpointPreset {
  let bestPreset = requestedPreset
  let bestArea = projectedArea(normalize(VIEWPOINT_PRESETS[requestedPreset].position), targetBounds.size)

  Object.keys(VIEWPOINT_PRESETS).forEach(function (presetName: string) {
    const preset = presetName as ViewpointPreset
    if (preset === requestedPreset) {
      return
    }

    const area = projectedArea(normalize(VIEWPOINT_PRESETS[preset].position), targetBounds.size)
    if (area > bestArea) {
      bestArea = area
      bestPreset = preset
    }
  })

  return bestPreset
}

function findOutwardPreset(
  sceneContext: ViewpointSceneContext,
  targetCenter: ViewpointVector3,
  fallback: ViewpointPreset
): ViewpointPreset {
  const outward = sub(targetCenter, sceneContext.sceneBounds.center)
  if (Math.sqrt(dot(outward, outward)) < 1e-6) {
    return fallback
  }

  const outwardDirection = normalize(outward)
  let bestPreset = fallback
  let bestScore = -Infinity

  Object.keys(VIEWPOINT_PRESETS).forEach(function (presetName: string) {
    const preset = presetName as ViewpointPreset
    const score = dot(normalize(VIEWPOINT_PRESETS[preset].position), outwardDirection)
    if (score > bestScore) {
      bestScore = score
      bestPreset = preset
    }
  })

  return bestPreset
}

function buildSeeds(options: PlannerOptions): SeedCandidate[] {
  const requestedPreset = options.requestedPreset
  const requestedSeed: SeedCandidate = {
    sourceSeed: 'requested',
    preset: requestedPreset,
    direction: normalize(VIEWPOINT_PRESETS[requestedPreset].position),
  }
  const silhouettePreset = findSilhouettePreset(requestedPreset, options.targetBounds)
  const silhouetteSeed: SeedCandidate = {
    sourceSeed: 'silhouette',
    preset: silhouettePreset,
    direction: normalize(VIEWPOINT_PRESETS[silhouettePreset].position),
  }
  const outwardPreset = findOutwardPreset(options.sceneContext, options.targetBounds.center, requestedPreset)
  const outwardSeed: SeedCandidate = {
    sourceSeed: 'outward',
    preset: outwardPreset,
    direction: normalize(VIEWPOINT_PRESETS[outwardPreset].position),
  }
  const seen: Record<string, boolean> = {}
  const seeds: SeedCandidate[] = []

  ;[requestedSeed, silhouetteSeed, outwardSeed].forEach(function (seed: SeedCandidate) {
    const key = [
      seed.direction.x.toFixed(3),
      seed.direction.y.toFixed(3),
      seed.direction.z.toFixed(3),
    ].join(':')

    if (seen[key]) {
      return
    }

    seen[key] = true
    seeds.push(seed)
  })

  return seeds
}

export function shouldKeepRequestedCandidate(requested: ViewCandidate, challenger: ViewCandidate): boolean {
  if (challenger.sourceSeed === 'requested') {
    return false
  }

  if (!requested.metrics || !challenger.metrics) {
    return false
  }

  if (requested.metrics.visibleSampleRatio >= REQUESTED_VISIBILITY_KEEP_THRESHOLD) {
    return true
  }

  return challenger.metrics.occluderOverlapRatio >= requested.metrics.occluderOverlapRatio - VISIBILITY_EPSILON
}

function expandSeed(
  seed: SeedCandidate,
  targetBounds: ViewpointBounds,
  explicitDistance: number | undefined,
  minDistance: number,
  yawOffsets: number[],
  pitchOffsets: number[]
): ViewCandidate[] {
  const baseAngles = directionToYawPitch(seed.direction)
  const candidates: ViewCandidate[] = []

  yawOffsets.forEach(function (yawOffset: number) {
    pitchOffsets.forEach(function (pitchOffset: number) {
      const pitchDeg = Math.max(-65, Math.min(65, baseAngles.pitchDeg + pitchOffset))
      const direction = yawPitchToDirection(baseAngles.yawDeg + yawOffset, pitchDeg)
      const distance = computeCandidateDistance(targetBounds, direction, seed.preset, explicitDistance, minDistance)

      candidates.push({
        sourceSeed: seed.sourceSeed,
        sourcePreset: seed.preset,
        direction: direction,
        targetCenter: targetBounds.center,
        distance: distance,
        liftOffset: 0,
        cameraPosition: buildCameraPosition(targetBounds.center, direction, distance, 0),
      })
    })
  })

  return candidates
}

export function selectView(options: PlannerOptions): ViewSelectionResult {
  const requestedDirection = normalize(VIEWPOINT_PRESETS[options.requestedPreset].position)
  const requestedSeed: SeedCandidate = {
    sourceSeed: 'requested',
    preset: options.requestedPreset,
    direction: requestedDirection,
  }
  const requestedCandidates = dedupeCandidates(
    expandSeed(requestedSeed, options.targetBounds, options.explicitDistance, options.minDistance, [0], [0, -10, 10])
  )
  const rankedRequested = rankCandidates(
    requestedCandidates,
    requestedDirection,
    options.sceneContext,
    options.targetBounds,
    options.targetElementIds
  )
  const requestedWithLifts = dedupeCandidates(
    rankedRequested.concat(buildLiftCandidates(rankedRequested, options.sceneContext, options.targetBounds.center))
  )
  const rankedRequestedFinal = rankCandidates(
    requestedWithLifts,
    requestedDirection,
    options.sceneContext,
    options.targetBounds,
    options.targetElementIds
  )

  if (rankedRequestedFinal[0].metrics!.quality !== 'degraded') {
    return {
      candidate: rankedRequestedFinal[0],
      metrics: rankedRequestedFinal[0].metrics!,
    }
  }

  const seeds = buildSeeds(options)
  let coarseCandidates: ViewCandidate[] = []
  seeds.forEach(function (seed: SeedCandidate) {
    coarseCandidates = coarseCandidates.concat(
      expandSeed(seed, options.targetBounds, options.explicitDistance, options.minDistance, [0, -90, -60, -30, 30, 60, 90], [0, -20, 20])
    )
  })

  const rankedCoarse = rankCandidates(
    dedupeCandidates(coarseCandidates),
    requestedDirection,
    options.sceneContext,
    options.targetBounds,
    options.targetElementIds
  )
  let fineCandidates = rankedCoarse.slice()

  rankedCoarse.slice(0, 3).forEach(function (candidate: ViewCandidate) {
    fineCandidates = fineCandidates.concat(
      expandSeed(
        {
          sourceSeed: candidate.sourceSeed,
          preset: candidate.sourcePreset,
          direction: candidate.direction,
        },
        options.targetBounds,
        options.explicitDistance,
        options.minDistance,
        [0, -15, 15],
        [0, -10, 10]
      )
    )
  })

  const rankedFine = rankCandidates(
    dedupeCandidates(fineCandidates),
    requestedDirection,
    options.sceneContext,
    options.targetBounds,
    options.targetElementIds
  )
  const liftCandidates = buildLiftCandidates(rankedFine, options.sceneContext, options.targetBounds.center)
  const rankedFinal = rankCandidates(
    dedupeCandidates(rankedFine.concat(liftCandidates)),
    requestedDirection,
    options.sceneContext,
    options.targetBounds,
    options.targetElementIds
  )
  const bestRequested = rankedRequestedFinal[0]
  const bestOverall = rankedFinal[0]

  if (shouldKeepRequestedCandidate(bestRequested, bestOverall)) {
    return {
      candidate: bestRequested,
      metrics: bestRequested.metrics!,
    }
  }

  return {
    candidate: bestOverall,
    metrics: bestOverall.metrics!,
  }
}
